import { arrayChunks } from "common/utilities/core/array";
import type * as I from "core/api/coreApiClient";
import {
  type EntitySequence as Sequence,
  type SequenceAlign,
  type SequenceFeature,
  SequenceType,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import { LABEL_COMPONENT } from "sequenceAnalyser/global/settings.ts";
import type { LetterOccurrence } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import type { ColorSchemesName } from "sequenceAnalyser/utilities/colors";
import {
  type ColorInfos,
  type ColorScheme,
  getMolecules,
  type SelectedColorScheme,
} from "sequenceAnalyser/utilities/colors";
import { removeAllGaps } from "sequenceAnalyser/utilities/selectionActions/removeGaps";
import {
  type FeatureDefinition,
  type FeatureDefinitions,
  type FeatureId,
  type SequenceAnalyserTableView,
  SequenceAnalyserTableViews,
} from "sequenceAnalyser/utilities/sequenceAnalyser.types";

export function buildFeatureName(feature: SequenceFeature | I.Feature): string {
  return `${feature.id}`;
}

/**
 * The features stored in the database and returned as SequenceFeature are a record
 * per feature found within a sequence: multiple records could point to the same feature type/feature name.
 * As we want a unique colors for those multiple records, we have to rely on the feature type/feature name
 * and not simply the feature id which is simply the record id.
 */
export function buildFeatureId(feature: SequenceFeature | I.Feature): FeatureId {
  const featureType = (feature as SequenceFeature).type || (feature as I.Feature).featureTypeLabel || "";
  return `${featureType}_${buildFeatureName(feature)}`;
}

export function longestSequence(sequences: SequenceAlign[]): SequenceAlign | undefined {
  return [...sequences].sort((a, b) => b.residues.length - a.residues.length)[0];
}

/**
 * Add gaps at the end of sequences to ensure they all have the same length.
 * This is not used when using the align endpoint but when the alignment is reset or inserting sequences manually.
 */
export function ensureAlignedSequencesSimilarLength(sequences: SequenceAlign[]) {
  const residuesLength = longestSequence(sequences)?.residues.length || 0;
  const trailingGapsRegex = /\.+$/;

  // Ensure sequences have the same length
  let newSequences = sequences.map(({ id, residues }) => ({
    id,
    residues: `${residues}${".".repeat(residuesLength - residues.length)}`,
  }));

  // Trim trailing gaps, this might happen when we hide a long sequences and all
  // remaining sequences have trailing gaps that were added earlier to match the
  // longest sequence length
  const trailingGapsCounts = newSequences.map((s) => {
    const match = s.residues.match(trailingGapsRegex);
    return match ? match[0].length : 0;
  });
  const shortestTrailingGapsCount = Math.min(...trailingGapsCounts);

  // Ensure sequences have the same length
  if (shortestTrailingGapsCount !== 0) {
    newSequences = newSequences.map(({ id, residues }) => ({
      id,
      residues: residues.slice(0, -shortestTrailingGapsCount),
    }));
  }

  // Return new sequences
  return newSequences;
}

export function getSelectedColorSchemeFromColorScheme(
  sessionSequenceType: SequenceType,
  name: ColorSchemesName,
  scheme: ColorScheme,
): SelectedColorScheme {
  let hydrophobicity: SelectedColorScheme["hydrophobicity"];

  if (scheme.hydrophobicValues && scheme.hydrophobic !== undefined) {
    const values = Object.values(scheme.hydrophobicValues);
    const max = Math.max(...values);
    // Default scheme hydrophobicity between hydrophobic threshold value and max
    hydrophobicity = [scheme.hydrophobic, max];
  }

  return {
    name,
    infos: getMolecules(sessionSequenceType).reduce<ColorInfos>((acc, residue) => {
      acc[residue.residue] = {
        name: residue.name,
        color: scheme.colors?.[residue.residue],
        hydrophobicValue: scheme.hydrophobicValues?.[residue.residue],
        show: true,
      };
      return acc;
    }, {}),
    conservation: 0,
    topOccurrenceOnly: false,
    hydrophobicity,
  };
}

// Transform a sequence as string to array: 'A B C D' to ['A', 'B', 'C', 'D']
export function residuesStringToArray(residues: string) {
  return residues.replace(/\s/g, "").split("");
}

// And cut it into chunks: [['A', 'B'], ['C', 'D']]
export function residuesStringToChunks(residues: string, residuesPerChunk: number): string[][] {
  const formattedResidues = residuesStringToArray(residues);
  return arrayChunks<string>(formattedResidues, residuesPerChunk);
}

export function sequenceAlignLength(sequence?: SequenceAlign) {
  return residuesStringToArray(sequence?.residues || "").length;
}

export function hasAnyFeatureDefinitionVisible(definitions: FeatureDefinitions) {
  return Object.values(definitions).some((def: FeatureDefinition) => def.visible);
}

export function formatResidueForView(
  residue: string,
  view: SequenceAnalyserTableView,
  firstRow: boolean,
  letterOccurrence: LetterOccurrence,
  maskingResidue: string,
) {
  return (view === SequenceAnalyserTableViews.abbreviated ||
    view === SequenceAnalyserTableViews.abbreviatedWithoutColors ||
    view === SequenceAnalyserTableViews.abbreviatedWithoutColorsAndFeaturesColors) &&
    !firstRow &&
    letterOccurrence.sameAsFirstLineLetter
    ? maskingResidue
    : residue;
}

export function isValidResidues(residues: string, sequenceType: SequenceType) {
  const characters = getMolecules(sequenceType).map((a) => a.residue);
  characters.push(".");
  if (sequenceType === SequenceType.protein) {
    characters.push("X");
    characters.push("*");
  }
  const regexp = new RegExp(`[^${characters.join("|")}]`, "i");
  return residues.match(regexp) === null;
}

/**
 * Determine residue position in its unaligned sequence by removing gaps
 */
export function unAlignedResiduePosition(sequences: SequenceAlign[], sequenceId: number, position: number) {
  const sourceAlignedSequence = sequences.find((s) => s.id === sequenceId) as SequenceAlign;
  const alignedSequenceUpToLastResiduePosition = sourceAlignedSequence.residues.slice(0, position);

  return removeAllGaps(alignedSequenceUpToLastResiduePosition).length;
}

// the label shown to the left of the individual sequences
export function getSequenceLabel(sequence: Sequence, labelComposition: string[], noSpace?: boolean) {
  const fields = labelComposition;

  // always show the name (which can be the gene name, the sequence ID...)
  let name = sequence.name;

  // switchable genome info
  if (fields.includes(LABEL_COMPONENT.GENOME)) {
    const genomeOrDatabase = sequence.qualifiedId !== "-" ? sequence.qualifiedId : sequence.chainInfoLabel;
    if (genomeOrDatabase) {
      name = genomeOrDatabase + ":" + name; // shoud be genomeName
    }
  }

  // ges.name and/or protein names
  if (fields.includes(LABEL_COMPONENT.SEQUENCE_NAME) && sequence.entityQualifiedId) {
    name += noSpace ? "|" : " ";
    // The sequence name is provided as entityQualifiedId which refers to the biologics syntax
    name += `(${sequence.entityQualifiedId})`; // should be sequenceName
  }

  if (
    (fields.includes(LABEL_COMPONENT.LOCATION) && sequence.entityLabel) ||
    (fields.includes(LABEL_COMPONENT.COMPLEMENT) && !sequence.directStrand)
  ) {
    name += noSpace ? "|[" : " [";
  }

  // switchable location info
  if (fields.includes(LABEL_COMPONENT.LOCATION) && sequence.entityLabel) {
    name += sequence.entityLabel;
  }

  // switchable reverse complement info
  if (fields.includes(LABEL_COMPONENT.COMPLEMENT) && !sequence.directStrand) {
    if (fields.includes(LABEL_COMPONENT.LOCATION)) {
      name += noSpace ? "," : ", ";
    }
    name += "complement";
  }

  if (
    (fields.includes(LABEL_COMPONENT.LOCATION) && sequence.entityLabel) ||
    (fields.includes(LABEL_COMPONENT.COMPLEMENT) && !sequence.directStrand)
  ) {
    name += "]";
  }

  return name;
}
